import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { catchError, Observable, of, ReplaySubject, switchMap, tap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { SERVER_API_URL } from 'app/app.constants';

@Injectable()
export class AuthService
{
    private _authenticated: boolean = false;
    private userIdentity: any | null = null;
    private authenticationState = new ReplaySubject<any | null>(1);
    private accountCache$?: Observable<any | null>;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    )
    {}

    head= new HttpHeaders({
    'Content-Type': 'application/json'});

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: any): Observable<any>
    {
        return this._httpClient.post(SERVER_API_URL + '/account/reset-password/init', email,{headers: this.head});
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(key: string,newPassword: string): Observable<any>
    {
        return this._httpClient.post(SERVER_API_URL + '/account/reset-password/finish', { key, newPassword },{headers: this.head});
    }
    public registerUser(user: any):Observable<any>{
        return this._httpClient.post<any>(SERVER_API_URL+ "/register",user,{headers: this.head})
    }
    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }
        return this._httpClient.post(SERVER_API_URL+'/loginAuth', credentials).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.token;
                localStorage.setItem('accessToken', response.token);
                // Set the authenticated flag to true
                this._authenticated = true;
                // Store the user on the user service
                this._userService.user = response.user;
                // Return a new observable with the response
                return of(response);
            })
        );
    }
    signInToken(token): any
    {
        localStorage.setItem('accessToken', token);
        // Set the authenticated flag to true
        this._authenticated = true;
        console.log(localStorage.getItem('accessToken'))
        return localStorage.getItem('accessToken')
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.post( SERVER_API_URL+ '/refreshToken', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>
                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                if ( response.accessToken )
                {
                    this.accessToken = response.accessToken;
                }
                this.authenticate(response)
                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { ine: string; email: string; password: string;  }): Observable<any>
    {
        return this._httpClient.post( SERVER_API_URL+'/register', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }
        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
    authenticate(identity: any | null): void {
        this.userIdentity = identity;
        this.authenticationState.next(this.userIdentity);
      }

      hasAnyAuthority(authorities: string[] | string): boolean {
        if (!this.userIdentity || !this.userIdentity.authorities) {
          return false;
        }
        if (!Array.isArray(authorities)) {
          authorities = [authorities];
        }
        return this.userIdentity.authorities.some((authority: string) => authorities.includes(authority));
      }


      isAuthenticated(): boolean {
        return this.userIdentity !== null;
      }

      getAuthenticationState(): Observable<any | null> {
        return this.authenticationState.asObservable();
      }



}
